import { StandardRequest, StandardResponse } from "./functions";
import { IUnifiedFilter } from "./filters";

/**
 * @swagger
 *  components:
 *    schemas:
 *      Share:
 *        type: object
 *        required: [ "sourceId", "sourceType", "targetId", "targetType", "role", "sharedBy", "sharedAt" ]
 *        properties:
 *          id:
 *            type: string
 *          sourceId:
 *            type: string
 *          sourceType:
 *            type: string
 *            enum: [ "memory", "group", "media", "contact" ]
 *          targetId:
 *            type: string
 *          targetType:
 *            type: string
 *            enum: [ "contact", "group" ]
 *          role:
 *            type: string
 *            enum: [ "viewer", "editor" ]
 *          sharedBy:
 *            type: string
 *          sharedAt:
 *            type: number
 */
/**
 * Raw persisted database document schema for a Share entity in Firestore (`shares/{id}`).
 */
export interface IShare {
  id?: string;
  sourceId: string; // ID of the thing being shared
  sourceType: "memory" | "group" | "media" | "contact" | "place" | "anchor";
  targetId: string; // UID of contact or ID of group
  targetContactId?: string; // Explicit alias for targetId when targetType === "contact"
  targetType: "contact" | "group";
  role: "owner" | "viewer" | "editor";
  sharedBy: string; // UID of the sharer
  sharedAt: number;
}

export type Share = IShare;

/**
 * @swagger
 *  components:
 *    schemas:
 *      ShareModel:
 *        allOf:
 *          - $ref: '#/components/schemas/Share'
 *          - type: object
 *            properties:
 *              targetDisplayName:
 *                type: string
 *              sharedByDisplayName:
 *                type: string
 *              sourceDisplayName:
 *                type: string
 */
export type ShareModel = Share & {
  targetDisplayName?: string;
  targetUserId?: string;
  sharedByDisplayName?: string;
  sourceDisplayName?: string;
};

export type ShareSummary = {
  id: string;
  displayName: string;
  type: "contact" | "group";
  role: "owner" | "viewer" | "editor";
  targetUserId?: string;
};

export type SharedToSummary = {
  id: string;
  displayName: string;
  type: "memory" | "group" | "media" | "contact" | "place" | "anchor";
};

/**
 * Common interface representing any domain entity that supports sharing,
 * role-based access control, and ownership.
 */
export interface ISharedEntity {
  id?: string;
  ownerId: string;
  createdAt: number;
  updatedAt?: number;
  userRole?: "owner" | "editor" | "viewer";
  canShare?: boolean;
  isOwner?: boolean;
  ownerDisplayName?: string;
  sharedWith?: ShareSummary[];
  sharedTo?: SharedToSummary[];
}

/**
 * @swagger
 *  components:
 *    schemas:
 *      ShareRequest:
 *        type: object
 *        required: [ "sourceId", "sourceType", "targetId", "targetType", "role" ]
 *        properties:
 *          sourceId:
 *            type: string
 *          sourceType:
 *            type: string
 *            enum: [ "memory", "group", "media", "contact" ]
 *          targetId:
 *            type: string
 *          targetType:
 *            type: string
 *            enum: [ "contact", "group" ]
 *          role:
 *            type: string
 *            enum: [ "viewer", "editor" ]
 */
export type ShareRequest = StandardRequest & {
  sourceId: string;
  sourceType: "memory" | "group" | "media" | "contact" | "place" | "anchor";
  targetId: string;
  targetContactId?: string; // Explicit alias for targetId when targetType === "contact"
  targetType: "contact" | "group";
  role: "viewer" | "editor";
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      ShareResponse:
 *        type: object
 *        properties:
 *          status:
 *            type: boolean
 *          timestamp:
 *            type: number
 *          shareId:
 *            type: string
 *          error:
 *            type: string
 */
export type ShareResponse = StandardResponse & {
  shareId?: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetSharesForSourceRequest:
 *        type: object
 *        required: [ "sourceId" ]
 *        properties:
 *          sourceId:
 *            type: string
 *          sourceType:
 *            type: string
 *            enum: [ "memory", "group", "media", "contact" ]
 */
export type GetSharesForSourceRequest = StandardRequest & {
  sourceId: string;
  sourceType: "memory" | "group" | "media" | "contact" | "place" | "anchor";
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetSharesForSourceResponse:
 *        type: object
 *        required: [ "status", "timestamp", "shares" ]
 *        properties:
 *          status:
 *            type: boolean
 *          timestamp:
 *            type: number
 *          shares:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/ShareModel'
 *          error:
 *            type: string
 */
export type GetSharesForSourceResponse = StandardResponse & {
  shares: ShareModel[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMySharesRequest:
 *        type: object
 *        properties: {}
 */
export type GetMySharesRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetMySharesResponse:
 *        type: object
 *        required: [ "status", "timestamp", "shares" ]
 *        properties:
 *          status:
 *            type: boolean
 *          timestamp:
 *            type: number
 *          shares:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/ShareModel'
 */
export type GetMySharesResponse = StandardResponse & {
  shares: ShareModel[];
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      UnshareRequest:
 *        type: object
 *        required: [ "shareId" ]
 *        properties:
 *          shareId:
 *            type: string
 */
export type UnshareRequest = StandardRequest & {
  shareId: string;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      ShareFilter:
 *        type: object
 *        properties:
 *          sourceId:
 *            type: string
 *          sourceType:
 *            type: string
 *            enum: [ "memory", "group", "media", "contact" ]
 *          targetId:
 *            type: string
 *          targetType:
 *            type: string
 *            enum: [ "contact", "group" ]
 */
export type ShareFilter = {
  sourceId?: string;
  sourceType?: "memory" | "group" | "media" | "contact";
  targetId?: string;
  targetType?: "contact" | "group";
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetFilteredSharesRequest:
 *        type: object
 *        properties:
 *          filter:
 *            $ref: '#/components/schemas/ShareFilter'
 */
export type GetFilteredSharesRequest = StandardRequest & {
  filter?: ShareFilter | IUnifiedFilter;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      GetFilteredSharesResponse:
 *        type: object
 *        required: [ "status", "shares" ]
 *        properties:
 *          status:
 *            type: boolean
 *          shares:
 *            type: array
 *            items:
 *              $ref: '#/components/schemas/ShareModel'
 *          error:
 *            type: string
 */
export type GetFilteredSharesResponse = StandardResponse & {
  shares: ShareModel[];
};
